<template>
  <div class="highlights-card-section">
    <header class="highlights-card-section__header">
      <figure v-if="highlightData.user" class="highlights-card-section__player-img">
        <img :src="highlightData.user.image ? highlightData.user.image : defaultIcon" alt="" />
      </figure>
      <h3
        v-if="highlightData.user"
        class="highlights-card-section__player-name"
        v-text="highlightData.user.name"
      />
      <time
        v-if="highlightData.editHighlightData"
        class="highlights-card-section__time"
        v-text="highlightData.editHighlightData.raceTime"
      />
    </header>
    <div
      class="highlights-card-section__video"
      v-if="isPlayingVideo"
      @pointerdown="visibleController"
    >
      <highlight-preview-video-player-section
        class="highlights-card-section__video-player"
        :screenOrientationType="screenOrientationType"
        :highlightData="highlightData"
      />

      <highlight-preview-controller-parts
        v-if="isVisibleController && isPc"
        class="highlights-card-section__video-overlay"
        :videoTimes="videoStatus"
        :screenOrientationType="screenOrientationType"
        :videoPaused="videoPaused"
        @videoPlayPauseChange="changeVideoPlayPause"
        @updateCurrentTime="updateCurrentTime"
      />
    </div>
    <div class="highlights-card-section__thumbnail" v-else>
      <button class="highlights-card-section__button" @click="handlePlayClicked">
        <span class="highlights-card-section__button-icon">
          <highlights-play-icon />
        </span>
        <img :src="highlightData.thumbnail" alt="" />
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, toRefs } from '@vue/composition-api'
import { EditHighlightData } from '@/store/stores/pageStore/RaceVideoPage/RaceVideoPageStore'
import HighlightsPlayIcon from '@/components/RaceVideoPage/RaceVideoHighlightsPane/parts/icons/HighlightsPlayIcon.vue'
import HighlightPreviewVideoPlayerSection from '@/components/RaceVideoPage/RaceVideoHighlightsPane/HighlightPreviewVideoPlayerSection.vue'
import HighlightPreviewControllerParts from '@/components/RaceVideoPage/RaceVideoHighlightsPane/HighlightPreviewControllerParts.vue'
import Logger from '@/util/logger/Logger'
import StoreUtil from '@/store/StoreUtil'
import DeviceInfo from '@/util/DeviceInfo'

/* eslint-disable @typescript-eslint/no-var-requires */
const defaultIcon = require('@/assets/img/icon/icon_user_default.png')

type DataType = {
  isPlayingVideo: boolean
  isVisibleController: boolean
  visibleControllerTimer: number
}

/**
 * レース動画再生画面 ハイライトカード作成: ハイライトカード セクション
 */
export default defineComponent({
  name: 'HighlightsCardSection',
  components: {
    HighlightPreviewControllerParts,
    HighlightsPlayIcon,
    HighlightPreviewVideoPlayerSection,
  },
  data(): DataType {
    return {
      isPlayingVideo: false,
      isVisibleController: false,
      visibleControllerTimer: 0,
    }
  },
  props: {
    /**
     * 画面向き
     */
    screenOrientationType: {
      type: String,
      default: 'portrait-primary',
    },
    /**
     * ハイライトの作成・編集で使用するデータ
     */
    highlightData: {
      type: Object as PropType<EditHighlightData>,
      required: true,
    },
  },
  setup() {
    const raceVideoPageStore = StoreUtil.useStore('RaceVideoPageStore')
    const { highlightStore } = raceVideoPageStore

    const isPc = DeviceInfo.isForPC()

    const { videoPlayer, videoStatus, videoPlayerStatus } = toRefs(
      highlightStore.raceVideoPageHighlightState,
    )
    const videoPaused = computed(() => videoPlayerStatus?.value?.playStatus === 'PAUSE')
    return {
      defaultIcon,
      videoPlayer,
      videoStatus,
      videoPlayerStatus,
      videoPaused,
      isPc,
    }
  },
  methods: {
    /**
     * サムネイルの再生ボタンが押下されたときの処理
     */
    handlePlayClicked() {
      this.isPlayingVideo = true
      this.visibleController()
      // コメント入力をフォーカスする
      const element = document.getElementById('comment')
      if (element) {
        element.focus()
      }
    },
    /**
     * コントローラーを表示
     */
    visibleController() {
      this.restartOverlayTimer()
      this.isVisibleController = true
    },
    /**
     * コントローラー：表示タイマーリセット
     */
    restartOverlayTimer() {
      if (this.visibleControllerTimer) {
        window.clearTimeout(this.visibleControllerTimer)
      }
      this.visibleControllerTimer = window.setTimeout(() => {
        this.isVisibleController = false
      }, 5000)
    },
    /**
     * 現在の動画の再生時間を指定された時間に変更する。
     * @param currentTime 動画再生時間
     */
    updateCurrentTime(currentTime: number) {
      this.videoPlayer?.setCurrentTime(currentTime)
    },
    /**
     * 動画再生・一時停止処理
     * @param mode 一時停止への変更の場合、 true が設定されて呼び出される。
     */
    async changeVideoPlayPause(mode: boolean) {
      if (mode) {
        await this.videoPlayer?.pause()
      } else {
        await this.videoPlayer?.play()
      }
      Logger.debug(
        `HighlightsCardPrats#changeVideoPlayPause: videoPausedStatus: ${this.videoPlayerStatus?.playStatus}, videoPaused: ${this.videoPaused}`,
      )
    },
  },
})
</script>

<style lang="scss" scoped>
.highlights-card-section {
  width: calc(100% - 16px);
  border-radius: 8px;
  background: $black80;
  color: $white;

  &__header {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    padding: 8px 8px 8px 16px;
  }

  &__player-img {
    width: 24px;
    height: 24px;

    img {
      width: 24px;
      height: 24px;
      object-fit: cover;
      border-radius: 50%;
    }
  }

  &__player-name {
    width: calc(100% - 117px);
    font-family: $fontSignup;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.53;
    letter-spacing: 0.04em;
    text-align: left;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  &__time {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 77px;
    margin-left: 8px;
    height: 24px;
    white-space: nowrap;
    text-align: center;
    font-size: 12px;
    line-height: 1.33;
    letter-spacing: 0.04em;
    border-radius: 2px;
    background: rgba(0, 0, 0, 70%);
  }

  &__video {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    background-color: $black;

    &-player {
      width: 100%;
      height: 100%;
      z-index: 1;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  &__thumbnail {
    width: 100%;
    aspect-ratio: 16 / 9;
    background-color: $black;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  &__button {
    position: relative;
    width: 100%;
    height: 100%;

    &-icon {
      position: absolute;
      top: 50%;
      left: 50%;
      z-index: 2;
      width: 32px;
      height: 32px;
      color: $white;
      transform: translate(-50%, -50%);
    }

    &::after {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      content: '';
      background-color: rgba(#000, 0.3);
    }
  }
}
</style>
